import { DestroyRef, Signal, computed, signal } from '@angular/core';

import { interval, Subscription } from 'rxjs';

export interface FlowCountdown {
    readonly remainingSeconds: Signal<number>;
    readonly display: Signal<string>;
    readonly expired: Signal<boolean>;
    setExpiresAt(expiresAt: string | null | undefined): void;
}

export function createFlowCountdown(destroyRef: DestroyRef): FlowCountdown {
    const remainingSeconds = signal(0);
    const display = computed(() => formatDuration(remainingSeconds()));
    const expired = computed(() => remainingSeconds() <= 0);

    let subscription: Subscription | null = null;

    const updateCountdown = (expiresAt: string | null | undefined): void => {
        subscription?.unsubscribe();
        subscription = null;

        const nextRemaining = getRemainingSeconds(expiresAt);
        remainingSeconds.set(nextRemaining);

        if (nextRemaining <= 0) {
            return;
        }

        subscription = interval(1000).subscribe(() => {
            const currentRemaining = getRemainingSeconds(expiresAt);
            remainingSeconds.set(currentRemaining);

            if (currentRemaining <= 0) {
                subscription?.unsubscribe();
                subscription = null;
            }
        });
    };

    destroyRef.onDestroy(() => {
        subscription?.unsubscribe();
        subscription = null;
    });

    return {
        remainingSeconds,
        display,
        expired,
        setExpiresAt: updateCountdown,
    };
}

function getRemainingSeconds(expiresAt: string | null | undefined): number {
    if (!expiresAt) {
        return 0;
    }

    const expiresAtMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresAtMs)) {
        return 0;
    }

    return Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
}

function formatDuration(totalSeconds: number): string {
    const safeSeconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(safeSeconds / 60).toString().padStart(2, '0');
    const seconds = (safeSeconds % 60).toString().padStart(2, '0');

    return `${minutes}:${seconds}`;
}
